import { Text, View } from "@react-pdf/renderer";
import { useTranslation } from "react-i18next";
import { DRUCKPFAHL, ZUGPFAHL } from "../../../constants";
import Degree from "../../Math/Units/Metric/Degree";
import KiloNewton from "../../Math/Units/Metric/KiloNewton";
import Metre from "../../Math/Units/Metric/Metre";
import Millimetre from "../../Math/Units/Metric/Millimetre";
import { DataRow, DataRowTextValue } from "../DataRow";
import { DataTable } from "../DataTable";
import { DataTableGroup } from "../DataTableGroup";
import { PageWrapper, styles, tableStyles } from "../SmartTitanProjektPDF";

export function GermanyAussereDruck({ data }) {
  const { t } = useTranslation("translation");
  let itemIndex = 1;
  let isCompression =
    data.Germany.Aussere.NachweisMantelreibung.belastungsartRichtung === DRUCKPFAHL;
  let isTension = data.Germany.Aussere.NachweisMantelreibung.belastungsartRichtung === ZUGPFAHL;
  return (
    <PageWrapper Deckblatt={data.Deckblatt}>
      <View>
        {/* Äußere Tragfähigkeit */}
        <Text style={styles.section}>{t("aeussre.outerLoadCarryingCapacity")}</Text>

        {/* Nachweis der Mantelreibung mit Erfahrungswerten */}
        <Text style={styles.sectionSubText}>{t("inner.fatigue.characteristicEffects")}</Text>
        <View style={tableStyles.tableContainer}>
          {/* Bemessungslast */}
          <DataRow
            item={{
              title: t("aeussre.designLoad"),
              value: data.Germany.Aussere.NachweisMantelreibung.bemessungslast,
              // F_{d}
              // https://latex.codecogs.com/png.image?\inline&space;\dpi{110}\bg{white}F_{d}
              // https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}F_{d}
              bezImage: {
                uri: "https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}F_{d}",
                height: 7,
                width: 10,
              },
              valueScale: 2,
              itemIndex: itemIndex++,
              valueUnit: <KiloNewton />,
            }}
          />

          {/* gewähltes Stahltragglied */}
          <DataRowTextValue
            item={{
              title: t("aeussre.selectedLoadCarryingElement"),
              value: data.Germany.Aussere.NachweisMantelreibung.gewaehltesStahltragglied,
              itemIndex: itemIndex++,
            }}
          />

          {/* Belastungsart/-Richtung */}
          <DataRowTextValue
            item={{
              title: t("aeussre.loadType"),
              value: t(
                data.Germany.Aussere.NachweisMantelreibung.belastungsartRichtung === DRUCKPFAHL
                  ? "inner.compressionPile"
                  : "inner.tensionPile"
              ),
              itemIndex: itemIndex++,
            }}
          />

          {/* Bohrerdurchmesser */}
          <DataRow
            item={{
              title: t("aeussre.drillBitDiameter"),
              value: data.Germany.Aussere.NachweisMantelreibung.bohrerdurchmesser,
              bezImage: {
                uri: "https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}D",
                height: 6,
                width: 7,
              },
              valueScale: 0,
              itemIndex: itemIndex++,
              valueUnit: <Millimetre />,
            }}
          />

          {/* Pfahlneigung zur Vertikalen */}
          <DataRow
            item={{
              title: t("aeussre.pileInclinationVertical"),
              value: data.Germany.Aussere.NachweisMantelreibung.pfahlneigungVertikalen,
              // \beta
              // https://latex.codecogs.com/png.image?\inline&space;\dpi{110}\bg{white}\beta
              // https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}\\beta
              bezImage: {
                uri: "https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}\\beta",
                height: 8,
                width: 5,
              },
              valueScale: 2,
              itemIndex: itemIndex++,
              valueUnit: <Degree />,
            }}
          />

          {/* Teilsicherheitsbeiwert */}
          <DataRow
            item={{
              title: t("aeussre.partialSafetyFactor"),
              value: data.Germany.Aussere.NachweisMantelreibung.teilsicherheitsbeiwert,
              // \gamma_{s}
              // https://latex.codecogs.com/png.image?\inline&space;\dpi{110}\bg{white}\gamma_{s}
              // https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}\\gamma_{s}
              bezImage: {
                uri: "https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}\\gamma_{s}",
                height: 6,
                width: 7,
              },
              valueScale: 1,
              itemIndex: itemIndex++,
            }}
          />
          {/* ModellFaktor */}
          {isTension && (
            <DataRow
              item={{
                title: t("aeussre.modelFactor"),
                value: data.Germany.Aussere.NachweisMantelreibung.modelFaktor,
                // \gamma_{s}
                // https://latex.codecogs.com/png.image?\inline&space;\dpi{110}\bg{white}\gamma_{s}
                // https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}\\gamma_{s}
                bezImage: {
                  uri: "https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}\\eta_{&space;_{M}}",
                  height: 6,
                  width: 7,
                },
                valueScale: 2,
                itemIndex: itemIndex++,
              }}
            />
          )}
        </View>

        {/* Bodenschichten */}
        <Text style={styles.sectionSubText}>{t("aeussre.soilLayers")}</Text>

        <DataTable
          item={data.Germany.Aussere.Bodenschichten}
          tableInput={data.Germany.Aussere.Bodenschichten.table_input}
          tableCalculated={data.Germany.Aussere.Bodenschichten.table_calulated}
          lreq={data.Germany.Aussere.Bodenschichten.lreq}
          sumRsdi={data.Germany.Aussere.Bodenschichten.sumRsdi}
        />

        <View style={tableStyles.tableContainer}>
          {/* erforderliche Pfahllänge */}
          <DataRow
            item={{
              title: t("aeussre.pileLengthRequired"),
              value: data.Germany.Aussere.Bodenschichten.erforderlichePfahllaenge,
              // L_{req}
              // https://latex.codecogs.com/png.image?\inline&space;\dpi{110}\bg{white}L_{req}
              // https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}L_{req}
              bezImage: {
                uri: "https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}L_{req}",
                height: 9,
                width: 15,
              },
              valueScale: 2,
              itemIndex: itemIndex++,
              valueUnit: <Metre />,
            }}
          />

          {/* gewählte Pfahllänge */}
          <DataRow
            item={{
              title: t("aeussre.pileLengthChoosen"),
              value: data.Germany.Aussere.Bodenschichten.gewaehltePfahllaenge,
              // L_{nom}
              // https://latex.codecogs.com/png.image?\inline&space;\dpi{110}\bg{white}L_{nom}
              // https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}L_{nom}
              bezImage: {
                uri: "https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}L_{nom}",
                height: 7,
                width: 18,
              },
              valueScale: 2,
              itemIndex: itemIndex++,
              valueUnit: <Metre />,
            }}
          />

          {/* Bemessungswert des Pfahlwiderstandes */}
          <DataRow
            item={{
              title: t("aeussre.designedPileResistance"),
              value: data.Germany.Aussere.Bodenschichten.bemessungswertPfahlwiderstandes,
              // R_{d,nom}
              // https://latex.codecogs.com/png.image?\inline&space;\dpi{110}\bg{white}R_{d,nom}
              // https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}R_{d,nom}
              bezImage: {
                uri: "https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}R_{d,nom}",
                height: 9,
                width: 25,
              },
              valueScale: 0,
              itemIndex: itemIndex++,
              valueUnit: <KiloNewton />,
            }}
          />

          {/* Ausnutzung */}
          <DataRow
            item={{
              title: t("aeussre.utilizationFactor"),
              value: data.Germany.Aussere.Bodenschichten.ausnutzung,
              // \eta
              // https://latex.codecogs.com/png.image?\inline&space;\dpi{110}\bg{white}\eta
              // https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}\\eta
              bezImage: {
                uri: "https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}\\eta",
                height: 6,
                width: 4,
              },
              valueScale: 2,
              itemIndex: itemIndex++,
              // valueUnit: <KiloNewton />,
              // \eta=\frac{F_d}{R_{d,nom}}
              // https://latex.codecogs.com/png.image?\inline&space;\dpi{110}\bg{white}\eta=\frac{F_d}{R_{d,nom}}
              // https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}\\eta=\\frac{F_d}{R_{d,nom}}
              formulaImage: {
                uri: "https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}\\eta=\\frac{F_d}{R_{d,nom}}",
                height: 13,
                width: 38,
              },
            }}
          />

          {/* <InvoiceTableBlankSpace rowsCount={25 - itemIndex} /> */}
        </View>

        {/* Bestimmung der Gesamtlänge */}
        <Text style={styles.sectionSubText}>{t("aeussre.determiningTotalLength")}</Text>

        <View style={tableStyles.tableContainer}>
          {/* Überstand */}
          <DataRow
            item={{
              title: t("aeussre.pileCapExtension"),
              value: data.Germany.Aussere.BestimmungGesamtlaenge.ueberstand,
              // L_0
              // https://latex.codecogs.com/png.image?\inline&space;\dpi{110}\bg{white}L_0
              // https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}L_0
              bezImage: {
                uri: "https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}L_0",
                height: 7,
                width: 8,
              },
              valueScale: 2,
              itemIndex: itemIndex++,
              valueUnit: <Metre />,
            }}
          />
          {/* Pfahllänge */}
          <DataRow
            item={{
              title: t("aeussre.pileLength"),
              value: data.Germany.Aussere.BestimmungGesamtlaenge.pfahllaenge,
              // L_{nom}
              // https://latex.codecogs.com/png.image?\inline&space;\dpi{110}\bg{white}L_{nom}
              // https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}L_{nom}
              bezImage: {
                uri: "https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}L_{nom}",
                height: 7,
                width: 18,
              },
              valueScale: 2,
              itemIndex: itemIndex++,
              valueUnit: <Metre />,
            }}
          />

          {/* gesamte Pfahllänge */}
          <DataRow
            item={{
              title: t("aeussre.totalPileLength"),
              value: data.Germany.Aussere.BestimmungGesamtlaenge.gesamtePfahllaenge,
              // L_{tot}
              // https://latex.codecogs.com/png.image?\inline&space;\dpi{110}\bg{white}L_{tot}
              // https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}L_{tot}
              bezImage: {
                uri: "https://latex.codecogs.com/png.image?\\inline&space;\\dpi{110}\\bg{white}L_{tot}",
                height: 7,
                width: 13,
              },
              valueScale: 2,
              itemIndex: itemIndex++,
              valueUnit: <Metre />,
            }}
          />
          {/* <InvoiceTableBlankSpace rowsCount={25 - itemIndex} /> */}
        </View>
      </View>
    </PageWrapper>
  );
}
